import { Actor, Canvas, Engine, Random, Vector, vec } from 'excalibur';
import { approach } from '../utils/angles';
import { SEEDS } from '../utils/constants';
import { Palette } from '../utils/palette';
import { css, sketchStroke, wobblyCirclePath, wobblyLinePath } from '../utils/sketch';

export interface SpeedButtonOptions {
  name: string;
  pos: Vector;
  radius: number;
}

export class SpeedButton extends Actor {
  readonly hitRadius: number;
  private readonly radius: number;
  private pressed = false;
  private pressAmount = 0;
  private time = 0;

  constructor(options: SpeedButtonOptions) {
    super({
      name: options.name,
      pos: options.pos,
      graphic: new Canvas({
        width: options.radius * 2 + 44,
        height: options.radius * 2 + 44,
        cache: false,
        draw: (ctx) => this.paint(ctx),
      }),
    });
    this.radius = options.radius;
    this.hitRadius = options.radius + 14;
  }

  setPressed(pressed: boolean): void {
    this.pressed = pressed;
  }

  isPointInside(x: number, y: number): boolean {
    const dx = x - this.pos.x;
    const dy = y - this.pos.y;
    return dx * dx + dy * dy <= this.hitRadius * this.hitRadius;
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    this.pressAmount = approach(this.pressAmount, this.pressed ? 1 : 0, dt * 9);
    const idle = 1 + Math.sin(this.time * 2.4) * 0.02;
    const scale = idle * (1 + this.pressAmount * 0.08);
    this.scale = vec(scale, scale);
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const mid = this.radius + 22;
    ctx.save();
    ctx.translate(mid, mid);
    paintSpeedButtonMark(ctx, new Random(SEEDS.speedButton), this.radius, this.pressAmount, this.time);
    ctx.restore();
  }
}

export function paintSpeedButtonMark(
  ctx: CanvasRenderingContext2D,
  rng: Random,
  radius: number,
  pressAmount: number,
  time: number,
): void {
  if (pressAmount > 0.02) {
    for (let i = 0; i < 3; i++) {
      ctx.save();
      ctx.globalAlpha = pressAmount * (0.26 - i * 0.07);
      ctx.fillStyle = css(Palette.pink);
      wobblyCirclePath(ctx, 0, 0, radius + 10 + i * 12, rng, 2, 32);
      ctx.fill();
      ctx.restore();
    }
  }

  const gradient = ctx.createRadialGradient(
    -radius * 0.3,
    -radius * 0.4,
    radius * 0.1,
    0,
    0,
    radius * 1.1,
  );
  gradient.addColorStop(0, css(Palette.yellow.lighten(0.35)));
  gradient.addColorStop(1, css(Palette.yellow));
  ctx.save();
  ctx.fillStyle = gradient;
  wobblyCirclePath(ctx, 0, 0, radius - 5, rng, radius * 0.045, 44);
  ctx.fill();
  ctx.restore();

  sketchStroke(ctx, rng, { color: Palette.ink, width: Math.max(3.4, radius * 0.055) }, (c) =>
    wobblyCirclePath(c, 0, 0, radius, rng, radius * 0.05, 52), 2, 3);

  const chevron = (offsetX: number): void => {
    const style = { color: Palette.deepInk, width: Math.max(4, radius * 0.085) };
    sketchStroke(ctx, rng, style, (c) =>
      wobblyLinePath(c, offsetX - radius * 0.15, -radius * 0.32, offsetX + radius * 0.17, 0, rng, radius * 0.014, 4), 1);
    sketchStroke(ctx, rng, style, (c) =>
      wobblyLinePath(c, offsetX + radius * 0.17, 0, offsetX - radius * 0.15, radius * 0.32, rng, radius * 0.014, 4), 1);
  };
  chevron(-radius * 0.22);
  chevron(radius * 0.22);

  if (pressAmount > 0.02) {
    const start = time * 6;
    ctx.save();
    ctx.globalAlpha = pressAmount * 0.85;
    ctx.strokeStyle = css(Palette.pinkDeep, 1);
    ctx.lineWidth = Math.max(3, radius * 0.07);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(0, 0, radius + 7, start, start + 1.4);
    ctx.stroke();
    ctx.restore();
  }
}
